// Patterns for find-and-redact.
//
// The scan runs in the worker over structured text, one line at a time,
// so every pattern here matches WITHIN a line — a phone number split
// across a line break is not found (the UI says so). Regex sources live
// here rather than in the worker so they can be unit-tested against real
// positives and negatives, which matters: a redaction pattern that
// over-matches destroys content the user meant to keep, and one that
// under-matches leaks the thing they were trying to hide.
//
// Every match is REVIEWABLE — a scan creates redaction marks, and marks
// only burn content when the file is exported.

export interface RedactPattern {
  id: string;
  label: string;
  /** Shown under the checkbox. */
  hint: string;
  source: string;
  flags: string;
  /** Tie-break order when two patterns cover the same span (see
   *  `dedupeMatches`). Lower is more specific and wins: a checksummed
   *  ABN should beat the phone pattern, which matches almost any run of
   *  digits. */
  rank?: number;
}

/** Digits-only Luhn check — the standard payment-card checksum. Cuts
 *  the false positives a bare 13–19 digit pattern would produce (order
 *  numbers, reference codes). */
export function luhn(digits: string): boolean {
  if (digits.length < 12) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const code = digits.charCodeAt(i);
    if (code < 48 || code > 57) return false;
    let d = code - 48;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/** Australian Tax File Number checksum (weighted mod 11). */
export function tfnValid(digits: string): boolean {
  const weights = [1, 4, 3, 7, 5, 8, 6, 9, 10];
  if (digits.length !== 9) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    sum += d * weights[i]!;
  }
  return sum % 11 === 0;
}

export const REDACT_PATTERNS: RedactPattern[] = [
  {
    id: "email",
    label: "Email addresses",
    hint: "name@example.com",
    source: "[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}",
    flags: "g",
  },
  {
    id: "phone",
    label: "Phone numbers",
    rank: 3,
    hint: "+61 2 1234 5678, (02) 1234 5678, 0412 345 678",
    // Optional country code, then 8–14 digits with spaces / dashes /
    // dots / one bracketed group. Anchored on a non-digit boundary so it
    // can't start mid-way through a longer number.
    source:
      "(?<![\\d])(?:\\+\\d{1,3}[ .-]?)?(?:\\(\\d{1,4}\\)[ .-]?)?\\d(?:[ .-]?\\d){6,13}(?![\\d])",
    flags: "g",
  },
  {
    id: "card",
    label: "Payment card numbers",
    hint: "13–19 digits passing the Luhn check",
    source: "(?<![\\d])(?:\\d[ -]?){12,18}\\d(?![\\d])",
    flags: "g",
  },
  {
    id: "tfn",
    label: "Tax file numbers (AU)",
    hint: "9 digits passing the TFN checksum",
    source: "(?<![\\d])\\d{3}[ ]?\\d{3}[ ]?\\d{3}(?![\\d])",
    flags: "g",
  },
  {
    id: "abn",
    label: "ABNs (AU)",
    hint: "11 digits, e.g. 51 824 753 556",
    source: "(?<![\\d])\\d{2}[ ]?\\d{3}[ ]?\\d{3}[ ]?\\d{3}(?![\\d])",
    flags: "g",
  },
  {
    id: "ssn",
    label: "US social security numbers",
    hint: "123-45-6789",
    source: "(?<![\\d-])\\d{3}-\\d{2}-\\d{4}(?![\\d-])",
    flags: "g",
  },
  {
    id: "ip",
    label: "IP addresses",
    hint: "IPv4, e.g. 192.168.0.1",
    source:
      "(?<![\\d.])(?:(?:25[0-5]|2[0-4]\\d|1?\\d?\\d)\\.){3}(?:25[0-5]|2[0-4]\\d|1?\\d?\\d)(?![\\d.])",
    flags: "g",
  },
  {
    id: "url",
    label: "Web addresses",
    hint: "https://example.com/path",
    source: "(?:https?://|www\\.)[^\\s,;)\\]]+",
    flags: "g",
  },
];

export function patternById(id: string): RedactPattern | undefined {
  return REDACT_PATTERNS.find((p) => p.id === id);
}

/**
 * Second-stage filter for patterns whose regex is deliberately loose.
 * Runs in the worker on each candidate; `true` keeps the match.
 */
export function acceptMatch(patternId: string, text: string): boolean {
  const digits = text.replace(/\D/g, "");
  if (patternId === "card") return luhn(digits);
  if (patternId === "tfn") return tfnValid(digits);
  if (patternId === "abn") return digits.length === 11 && abnValid(digits);
  if (patternId === "phone") {
    // A dotted quad is an IP address, not a phone number — the phone
    // pattern allows "." as a separator, so it matches one otherwise.
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(text)) return false;
    // 7 digits is the shortest real subscriber number; 15 is the E.164
    // ceiling. Anything longer is an account or reference number.
    return digits.length >= 7 && digits.length <= 15;
  }
  return true;
}

/** Patterns whose match is "a run of digits" and therefore can land in
 *  the middle of a longer number. */
const DIGIT_RUN_PATTERNS = new Set(["phone", "card", "tfn", "abn", "ssn"]);

/**
 * Reject a numeric match that is part of a longer number.
 *
 * Regex boundaries can't see past a separator, so `1234 5678 9012 3456`
 * (a 16-digit reference) yields a perfectly well-formed 12-digit "phone
 * number" — the character after the match is a space, and the lookahead
 * is satisfied. Redacting the first three groups of someone's order
 * number is exactly the kind of damage a scan must not do.
 */
export function partOfLongerNumber(
  patternId: string,
  line: string,
  start: number,
  end: number
): boolean {
  if (!DIGIT_RUN_PATTERNS.has(patternId)) return false;
  return /\d[ .\-]?$/.test(line.slice(0, start)) || /^[ .\-]?\d/.test(line.slice(end));
}

/** ABN checksum: subtract 1 from the first digit, weighted mod 89. */
export function abnValid(digits: string): boolean {
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  if (digits.length !== 11) return false;
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    sum += (i === 0 ? d - 1 : d) * weights[i]!;
  }
  return sum % 89 === 0;
}

/** Escape a user's literal phrase for use inside a RegExp. */
export function escapeLiteral(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export interface LineMatch {
  /** Character offsets within one structured-text line. */
  start: number;
  end: number;
  patternId: string;
  text: string;
}

/**
 * One mark per span. Patterns overlap constantly — a phone number is
 * also eleven digits, an email contains a domain that looks like a URL —
 * and two marks over the same glyphs would mean two rows to review and
 * two annotations to delete.
 *
 * Longest match wins; ties go to the earlier pattern, which is the order
 * `compilePatterns` produced (the user's own phrase first).
 */
export function dedupeMatches(matches: LineMatch[]): LineMatch[] {
  const ranked = matches
    .map((m, i) => ({ m, i }))
    .sort((a, b) => b.m.end - b.m.start - (a.m.end - a.m.start) || a.i - b.i);
  const kept: LineMatch[] = [];
  for (const { m } of ranked) {
    if (kept.some((k) => m.start < k.end && k.start < m.end)) continue;
    kept.push(m);
  }
  return kept.sort((a, b) => a.start - b.start);
}

export interface ScanRequest {
  /** Literal phrase to find (optional). */
  phrase?: string;
  matchCase?: boolean;
  wholeWord?: boolean;
  /** Ids from REDACT_PATTERNS. */
  patternIds?: string[];
  /** A regular expression the user typed. */
  customSource?: string;
}

export interface CompiledPattern {
  id: string;
  label: string;
  source: string;
  flags: string;
}

/** Turn the dialog's state into the pattern list the worker runs.
 *  Throws on an invalid custom expression so the UI can show why. */
export function compilePatterns(req: ScanRequest): CompiledPattern[] {
  const out: CompiledPattern[] = [];
  const phrase = req.phrase?.trim();
  if (phrase) {
    const body = escapeLiteral(phrase);
    out.push({
      id: "phrase",
      label: `“${phrase}”`,
      source: req.wholeWord ? `\\b${body}\\b` : body,
      flags: req.matchCase ? "g" : "gi",
    });
  }
  const chosen = (req.patternIds ?? [])
    .map((id) => patternById(id))
    .filter((p): p is RedactPattern => p !== undefined)
    // Stable sort by specificity so the dedupe tie-break favours the
    // checksummed patterns over the catch-all digit runs.
    .map((p, i) => ({ p, i }))
    .sort((a, b) => (a.p.rank ?? 2) - (b.p.rank ?? 2) || a.i - b.i);
  for (const { p } of chosen) {
    out.push({ id: p.id, label: p.label, source: p.source, flags: p.flags });
  }
  const custom = req.customSource?.trim();
  if (custom) {
    // Compile here (in the UI) so an invalid expression is a message,
    // not a worker exception.
    new RegExp(custom, "g");
    out.push({
      id: "custom",
      label: "Custom expression",
      source: custom,
      flags: req.matchCase ? "g" : "gi",
    });
  }
  return out;
}
